// src/services/regxPatientCreate.js
//
// "Add to RegX" from the weekly report (2026-10-09): a coordinator adds a
// patient who isn't in RegX yet, straight into a box of their choice. It does
// what the Coordinator page's Add Patient does (same fields, same checks, a
// `consents` record with signedAt so the CRC page lists it), plus:
//   - the box is chosen (Add Patient always uses Lung Center Referrals);
//   - a coordinator box stamps movedToCoordinatorAt, as the patient page does,
//     so the stale-box reminder ages from the add;
//   - Enrolled works like the patient page: the coordinator picks the
//     study(ies), entries are "pending" for an admin's approval
//     (enrollment_requests), and if every study is diagnostics or closed the
//     patient goes to Lung Center Referrals instead (shared rule);
//   - an audit event (MRN last 4 only).
// Never writes the Google Sheet (Add Patient doesn't either). Never changes an
// existing patient.
//
// Pure parts (box list, validation, the record) are exported and tested in
// regxPatientCreate.test.js.

import { addDoc, collection, getDocs, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../firebase";
import { getConsentsPath, getDuplicatesPath } from "./firestorePaths";
import { activeSiteUsesLegacySheet } from "./activeTenant";
import { fetchSheetPatients } from "./sheetPatientsService";
import { findPotentialDuplicates, sameMrnNumber } from "./patientDuplicateMatch";
import { logPatientAuditEvent, getMrnLast4 } from "./patientAuditService";
import { createEnrollmentRequests } from "./enrollmentRequestService";
import {
  coordinatorPickEntries,
  shouldReturnToReferrals,
  stampReturn,
  ENROLLED_STATUS,
  REFERRALS_STATUS,
  RETURN_REASON_TEXT,
} from "./enrollmentRules";
import { CRC_BOX_STATUSES, COORDINATOR_BOX_STAGES, coordinatorBoxStatus } from "../constants/patientBoxes";
import { DIAGNOSIS_OPTIONS, statusLabel } from "../constants/diseaseOptions";

/**
 * Every CRC-page box, in the page's order, as { value (stored status), label (shown) }.
 * @param {string[]} coordinatorNames the site's active coordinators
 */
export const regxBoxOptions = (coordinatorNames = []) => [
  ...CRC_BOX_STATUSES.map((v) => ({ value: v, label: v, group: "Boxes" })),
  ...DIAGNOSIS_OPTIONS.map((dx) => {
    const v = `Future Studies - ${dx}`;
    return { value: v, label: statusLabel(v), group: "Future Studies" };
  }),
  ...coordinatorNames
    .map((n) => String(n || "").trim())
    .filter(Boolean)
    .flatMap((name) =>
      COORDINATOR_BOX_STAGES.map((stage) => {
        const v = coordinatorBoxStatus(stage, name);
        return { value: v, label: v, group: "Coordinator boxes" };
      })
    ),
];

export const isCoordinatorBox = (status) =>
  COORDINATOR_BOX_STAGES.some((stage) => String(status || "").startsWith(`${stage} (`));

/** Problems with the form, in plain words (empty = ready). Same rules as Add Patient. */
export const validateNewPatient = ({ name, dob, mrn, diagnosis, status } = {}) => {
  const errors = [];
  if (!String(name || "").trim()) errors.push("Enter the patient's name.");
  if (!String(dob || "").trim()) errors.push("Enter the date of birth.");
  if (!/^\d{9}$/.test(String(mrn || "").trim())) errors.push("MRN must be exactly 9 digits.");
  if (!Array.isArray(diagnosis) || diagnosis.length === 0) errors.push("Pick at least one diagnosis.");
  if (!String(status || "").trim()) errors.push("Pick a box.");
  return errors;
};

/**
 * Enrolled: the picked studies as pending entries, and where the patient goes
 * (the patient page's rule: all diagnostics/closed -> Lung Center Referrals).
 */
export const planEnrollment = (picks, studiesById, { at, by } = {}) => {
  let entries = coordinatorPickEntries(picks, { at, by });
  const outcome = shouldReturnToReferrals(entries, studiesById);
  if (!outcome.move) return { status: ENROLLED_STATUS, entries, outcome, diagnosticsFlag: false };
  entries = stampReturn(entries, studiesById, { at, by, reason: outcome.reason });
  // Same as the patient page: keep the DIAGNOSTICS flag working (not for "closed").
  return { status: REFERRALS_STATUS, entries, outcome, diagnosticsFlag: outcome.reason !== "closed" };
};

/** The consents record (without server timestamps). */
export const buildNewPatientRecord = ({ name, dob, mrn, diagnosis, status }, { at, by, enrollment = null } = {}) => {
  const record = {
    name: String(name).trim(),
    dob: String(dob).trim(),
    mrn: String(mrn).trim(),
    diagnosis: [...diagnosis],
    status: enrollment ? enrollment.status : status,
    addedFrom: "WeeklyReport",
    addedBy: by || "",
  };
  if (isCoordinatorBox(record.status)) record.movedToCoordinatorAt = at;
  if (enrollment) {
    record.enrolledStudies = enrollment.entries;
    if (enrollment.diagnosticsFlag) {
      record.enrolledInDiagnostics = true;
      record.enrolledInDiagnosticsAt = at;
      record.enrolledInDiagnosticsBy = by || "";
    }
  }
  return record;
};

/** Everyone already in RegX for the duplicate check: consents, duplicates and (Dubois) the Sheet. */
export const loadDuplicateCandidates = async () => {
  const [cons, dups] = await Promise.all([
    getDocs(collection(db, getConsentsPath())),
    getDocs(collection(db, getDuplicatesPath())),
  ]);
  const list = [
    ...cons.docs.map((d) => ({ id: d.id, ...d.data(), source: "firestore", sourceLabel: "Referrals" })),
    ...dups.docs.map((d) => ({ id: d.id, ...d.data(), source: "duplicates", sourceLabel: "Duplicates" })),
  ];
  if (activeSiteUsesLegacySheet()) {
    try {
      const rows = await fetchSheetPatients();
      rows.forEach((r) => list.push({ ...r, id: r.token || r.id, source: "sheet", sourceLabel: "Legacy Sheet" }));
    } catch (e) {
      // Same as Reception: if the Sheet can't be read, the RegX records still count.
      console.warn("Sheet unavailable for the duplicate check:", e);
    }
  }
  return list;
};

/**
 * The duplicate check, same rule as Add Patient: an exact MRN is refused
 * (`sameMrn`), a likely match (same DOB + same name, or MRN one digit off) is
 * a warning (`likely`).
 */
export const checkDuplicates = (form, candidates) => {
  const sameMrn = candidates.filter((c) => c && c.mrn && sameMrnNumber(c.mrn, form.mrn));
  const likely = sameMrn.length
    ? []
    : findPotentialDuplicates({ name: form.name, dob: form.dob, mrn: form.mrn }, candidates);
  return { sameMrn, likely };
};

export class DuplicatePatientError extends Error {
  constructor(result) {
    super(result.sameMrn.length ? "This MRN is already in RegX." : "A possible match is already in RegX.");
    this.result = result;
  }
}

/**
 * Create the patient. Re-loads everyone and re-checks right before writing.
 * `allowLikely`: the person saw the likely matches and chose "Add anyway".
 * `enrollment`: from planEnrollment, when the box is Enrolled.
 * Returns { id, record }.
 */
export const createRegxPatient = async (form, { allowLikely = false, enrollment = null } = {}) => {
  const errors = validateNewPatient(form);
  if (errors.length) throw new Error(errors.join(" "));
  const result = checkDuplicates(form, await loadDuplicateCandidates());
  if (result.sameMrn.length || (result.likely.length && !allowLikely)) throw new DuplicatePatientError(result);

  const by = auth.currentUser?.email || "unknown";
  const at = new Date().toISOString();
  const record = buildNewPatientRecord(form, { at, by, enrollment });
  const ref = await addDoc(collection(db, getConsentsPath()), {
    ...record,
    // The CRC page lists patients ordered by signedAt (a record without it is left out).
    signedAt: serverTimestamp(),
    createdAt: serverTimestamp(),
  });

  const base = {
    patientId: ref.id,
    patientName: record.name,
    mrnLast4: getMrnLast4(record.mrn),
    source: "WeeklyReport",
    actorEmail: by,
  };
  await logPatientAuditEvent({
    ...base,
    title: `Added to RegX from a weekly report (${statusLabel(record.status)})`,
    action: "PATIENT_CREATED_FROM_WEEKLY_REPORT",
    changes: [{ field: "status", from: null, to: record.status }],
    metadata: {
      box: record.status,
      ...(result.likely.length ? { createdDespiteMatches: result.likely.map((m) => `${m.patient.source}/${m.patient.id}`) } : {}),
    },
  });

  if (enrollment) {
    // Same audit events and admin approval requests as the patient page's Save.
    for (const e of enrollment.entries) {
      await logPatientAuditEvent({
        ...base,
        title: "Enrolled in Study",
        action: "ENROLLED_STUDY_ADDED",
        changes: [{ field: "enrolledStudies", from: null, to: e.studyName || e.studyId }],
        metadata: { studyId: e.studyId, disease: e.disease || null, approval: e.approval || null },
      });
    }
    if (enrollment.outcome?.move) {
      await logPatientAuditEvent({
        ...base,
        title: "Returned to Lung Center Referrals",
        action: "RETURNED_TO_REFERRALS",
        changes: [{ field: "status", from: ENROLLED_STATUS, to: REFERRALS_STATUS }],
        metadata: {
          reason: enrollment.outcome.reason,
          reasonText: RETURN_REASON_TEXT[enrollment.outcome.reason] || null,
          studies: enrollment.entries.map((e) => ({ studyId: e.studyId, studyName: e.studyName })),
        },
      });
    }
    const failed = await createEnrollmentRequests({
      patientId: ref.id,
      source: "firestore",
      patientName: record.name,
      dob: record.dob,
      mrn: record.mrn,
      entries: enrollment.entries,
    });
    if (failed) {
      window.alert(
        "The patient was added, but the approval request couldn't be sent to an admin. Please let an admin know."
      );
    }
  }
  return { id: ref.id, record };
};
